import { Form, Field } from "react-final-form";
import { type RefObject, useImperativeHandle, useRef, useState } from "react";
import { MessageCircleMore } from "lucide-react";
import { IconTaggedPlayers } from "../../assets/IconTaggedPlayers";
import { IconTaggedPlayersAndParents } from "../../assets/IconTaggedPlayersAndParents";
import { IconAllPlayers } from "../../assets/IconAllPlayers";
import { IconEntireTeam } from "../../assets/IconEntireTeam";
import { EventComment, TeamPlayer } from "../../../types";
import { _ } from "i18n";
import type { Keys } from "i18n/locales/types";
import { FormApi } from "final-form";
import { MentionsInput, Mention, SuggestionDataItem } from 'react-mentions';
import { mentionStyles, mentionHighlightStyle } from './MentionStyles';
import './video-comments.css';
import { PlayerPickerPopover } from './PlayerPickerPopover';
import { getTaggedPlayerIds, insertMention, removeMention } from './mentionHelpers';

const defaultValues: EventComment = {
	text: "",
	lockerRoomVisibility: "players",
	setChoicesAsDefault: false,
	visibleToAll: false,
};

export interface CommentFormDefaults {
	lockerRoomVisibility?: string | null;
	visibleToAll?: boolean;
}

export interface VideoCommentFormProps {
	ref: RefObject<unknown>;
	onSubmit: (values: EventComment) => void;
	teamPlayers: TeamPlayer[];
	commentFormDefaults?: CommentFormDefaults | null;
}

export function VideoCommentsForm({ ref, onSubmit, teamPlayers, commentFormDefaults }: VideoCommentFormProps) {
	const formRef = useRef<FormApi<EventComment> | null>(null);
	const [showPlayerPicker, setShowPlayerPicker] = useState(false);
	const prevHasMentions = useRef(false);

	const deliveryOptions = [
		{ id: "tagged_players", label: "discover.gameComment.form.taggedPlayers", Icon: IconTaggedPlayers },
		{ id: "tagged_players_and_their_parents", label: "discover.gameComment.form.taggedPlayersAndParents", Icon: IconTaggedPlayersAndParents },
		{ id: "players", label: "discover.gameComment.form.delivery.players", Icon: IconAllPlayers },
		{ id: "players_and_parents", label: "discover.gameComment.form.delivery.entireTeam", Icon: IconEntireTeam },
	];

	useImperativeHandle(
		ref,
		() => ({
			submit() {
				formRef.current?.submit();
			},
		}),
		[],
	);

	const handleSubmit = (values: EventComment, form: FormApi<EventComment>) => {
		onSubmit(values);
		form.reset();
	};

	interface MentionOption extends SuggestionDataItem {
		avatar?: string | null;
	}

	const options: MentionOption[] = teamPlayers.map(player => ({
		id: String(player.id),
		display: player.name,
		avatar: player.photo,
	}));

	const isMentionOnlyVisibility =
		commentFormDefaults?.lockerRoomVisibility === "tagged_players" ||
		commentFormDefaults?.lockerRoomVisibility === "tagged_players_and_their_parents";

	const initialValues: EventComment = {
		...defaultValues,
		...(commentFormDefaults?.lockerRoomVisibility != null && !isMentionOnlyVisibility && {
			lockerRoomVisibility: commentFormDefaults.lockerRoomVisibility as EventComment["lockerRoomVisibility"],
		}),
		...(commentFormDefaults?.visibleToAll != null && {
			visibleToAll: commentFormDefaults.visibleToAll,
		}),
	};

	return (
		<Form
			onSubmit={(values, form) => handleSubmit(values, form as FormApi<EventComment>)}
			initialValues={initialValues}
			render={({ handleSubmit, form, values }) => {
				formRef.current = form;

				const taggedIds = getTaggedPlayerIds(values.text ?? "");
				const taggedPlayers = teamPlayers.filter(p => taggedIds.has(p.id));

				const mentionRegex = /@\[(.+?)\]\((\d+)\)/g;
				const matches = (values.text ?? "").match(mentionRegex) ?? [];
				const hasMentions = matches.length > 0;

				const visibleDeliveryOptions = deliveryOptions.filter(option => {
					if (hasMentions) return true;
					return option.id === "players" || option.id === "players_and_parents";
				});

				return (
					<form onSubmit={handleSubmit}>
						<div className="flex w-full gap-4 mb-4">
							<div className="pt-6 w-full">
								<div className="flex items-center justify-between mb-3">
									<h3 className="text-size-base leading-sm font-medium text-Text-primary">
										{_("discover.gameComment.form.comment.title")}
									</h3>
									<div className="relative">
										<button
											type="button"
											onClick={() => setShowPlayerPicker((v) => !v)}
											className="text-sm font-medium text-secondary rounded-md px-3 py-1.5 bg-Surface-action-neutral"
										>
											{_("discover.gameComment.form.tagPlayer.button" as keyof Keys)}
										</button>
										{showPlayerPicker && (
											<PlayerPickerPopover
												players={teamPlayers}
												taggedPlayerIds={taggedIds}
												onSelect={(player) => {
													form.change("text", insertMention(values.text ?? "", player));
													if (!prevHasMentions.current) {
														form.change("lockerRoomVisibility", "tagged_players");
														prevHasMentions.current = true;
													}
													setShowPlayerPicker(false);
												}}
												onRemove={(player) => {
													const newText = removeMention(values.text ?? "", player.id);
													form.change("text", newText);
													const remaining = getTaggedPlayerIds(newText);
													if (remaining.size === 0 && prevHasMentions.current) {
														form.change("lockerRoomVisibility", "players");
														prevHasMentions.current = false;
													}
													setShowPlayerPicker(false);
												}}
												onClose={() => setShowPlayerPicker(false)}
											/>
										)}
									</div>
								</div>

								<Field name="text">
									{({ input, meta }) => (
										<MentionsInput
											{...input}
											onChange={(event, _newValue, _newPlainTextValue, mentions) => {
												input.onChange(event);
												const newHasMentions = mentions.length > 0;
												if (newHasMentions && !prevHasMentions.current) {
													formRef.current?.change("lockerRoomVisibility", "tagged_players");
												} else if (!newHasMentions && prevHasMentions.current) {
													formRef.current?.change("lockerRoomVisibility", "players");
												}
												prevHasMentions.current = newHasMentions;
											}}
											placeholder={_("discover.gameComment.form.comment.label")}
											style={mentionStyles}
										>
											<Mention
												trigger="@"
												data={(search, callback) => {
													if (!search) {
														callback(options.slice(0, 6));
														return;
													}
													const filtered = options
														.filter(o => o.display?.toLowerCase().includes(search.toLowerCase()))
														.slice(0, 6);
													callback(filtered);
												}}
												markup='@[__display__](__id__)'
												displayTransform={(id, display) => `@${display}`}
												style={mentionHighlightStyle}
												renderSuggestion={(entry: MentionOption, search, highlightedDisplay) => (
													<>
														{entry.avatar ? (
															<img
																src={entry.avatar}
																alt={entry.display}
																className="w-8 h-8 rounded-full object-cover shrink-0"
															/>
														) : (
															<div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-semibold shrink-0">
																{entry.display?.charAt(0).toUpperCase()}
															</div>
														)}
														<span className="text-sm text-gray-800">{highlightedDisplay}</span>
													</>
												)}
											/>
										</MentionsInput>
									)}
								</Field>
							</div>
						</div>

						<Field name="lockerRoomVisibility">
							{({ input }) => {
								return (
									<div className="flex w-full gap-4 mb-4">
										<div className="pt-6 w-full">
											<div className="space-y-4">
												<div className="flex items-center gap-2">
													<MessageCircleMore className="w-6 h-6 text-blue-500" />
													<h3 className="text-size-base leading-sm font-medium text-Text-primary">
														{_("discover.gameComment.form.deliverChar.title")}
													</h3>
												</div>
												<p className="text-size-base leading-sm font-light text-Text-tertiary">
													{_("discover.gameComment.form.deliverChar.description")}
												</p>

												<div className="grid grid-cols-2 gap-3">
													{visibleDeliveryOptions.map((option) => {
														const { Icon } = option;
														const isSelected = input.value === option.id;

														return (
															<label
																key={option.id}
																style={isSelected ? { backgroundColor: "#E6F9FF", borderColor: "#03C5FF", borderWidth: "1px", borderStyle: "solid" } : { backgroundColor: "#ffffff", borderColor: "#E5E7EB", borderWidth: "1px", borderStyle: "solid" }}
																className="relative cursor-pointer rounded-2xl text-center transition-colors duration-150 p-4"
															>
																<input
																	type="radio"
																	name="lockerRoomVisibility"
																	value={option.id}
																	checked={isSelected}
																	onChange={input.onChange}
																	onBlur={input.onBlur}
																	className="sr-only"
																/>
																<div
																	style={isSelected ? { backgroundColor: "#03C5FF" } : { borderColor: "#D1D5DB" }}
																	className={`absolute top-3 left-3 w-5 h-5 rounded-full flex items-center justify-center ${isSelected ? "" : "border-2 bg-white"}`}
																>
																	{isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
																</div>
																<div className="mx-auto mb-3 mt-3 w-20 h-20">
																	<Icon />
																</div>
																<div
																	className="gradient-text font-primary text-size-sm font-semibold leading-xsm tracking-normal text-center"
																>
																	{_(option.label as keyof Keys)}
																</div>
															</label>
														);
													})}
												</div>
											</div>
										</div>
									</div>
								);
							}}
						</Field>

				<Field name="visibleToAll" type="checkbox">
					{({ input }) => (
						<div className="flex w-full items-center justify-between gap-4 mb-4 ps-1 pt-3">
							<span className="text-Text-primary text-size-sm leading-xsm font-medium">
								{_("discover.gameComment.form.visibilityAll.label")}
							</span>
							<button
								type="button"
								role="switch"
								aria-checked={!!input.checked}
								onClick={() => input.onChange(!input.checked)}
								className="toggle-switch"
							>
								<span className="toggle-knob" />
							</button>
						</div>
					)}
				</Field>

					<Field name="setChoicesAsDefault" type="checkbox">
							{({ input }) => (
								<div className="flex w-full gap-4 mb-4">
									<div className="inline-flex items-center gap-2 ps-1">
										<input
											{...input}
											type="checkbox"
											className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
										/>
										<div className="flex flex-col">
											<span className="text-Text-tertiary text-size-sm leading-xsm font-light mt-1">
												{_("discover.gameComment.form.rememberDefault.label")}
											</span>
										</div>
									</div>
								</div>
							)}
						</Field>
					</form>
				);
			}}
		/>
	);
}
